import {
  AuditSettingsDto,
  AuditSettingsPatchDto,
  QueryFactory,
} from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { useRegisterUnsavedChanges } from '@lmt/unicorn-shared/app/components/tenant-settings/components/useRegisterUnsavedChanges';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  maxRule,
  minRule,
  positiveRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './../SettingsBlock.module.scss';
import { useEventCallback } from '@mui/material';
import { TenantSettingsKeys } from './../types';

export const AuditSettings: FC = React.memo(function AuditSettings() {
  const i18n = useTranslation();
  const i18nSettings = useScopedTranslation('tenant_settings.audit_settings');

  const { data: auditSettings, refetch: refetch } =
    QueryFactory.AuditSettingsQuery.useGetQuery();

  const form = useAdvancedForm<AuditSettingsDto>(
    useCallback(async (data: AuditSettingsDto) => {
      const dto: AuditSettingsPatchDto = {
        ...data,
      };
      await QueryFactory.AuditSettingsClient.patch(dto);
      await refetch();
    }, []),
    auditSettings,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );
  const { formState, reset, register, watch } = form;

  const onReset = useEventCallback(() => {
    reset(auditSettings);
  });

  useEffect(() => {
    onReset();
  }, [auditSettings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.Audit,
    form,
  );

  const [cleanupEnabled] = watch(['cleanupEnabled']);

  return (
    <form onSubmit={form.handleSubmitDefault}>
      <div className={styles.container} data-test-id="audit-settings-container">
        <div className={styles.header}>
          <div className={styles.title}>{i18nSettings.t('title')}</div>
          <div className={styles.optionContainer}>
            <Button
              disabled={!isUnsavedChanges}
              title={i18nSettings.t('reset_button_title')}
              className={styles.option}
              color={ButtonColor.Primary}
              onClick={onReset}
            />
            <SubmitButton
              disabled={!isUnsavedChanges}
              title={i18nSettings.t('submit_button_title')}
              submitting={formState.isSubmitting}
              className={styles.option}
            />
          </div>
        </div>
        <Field className={styles.field} title={''}>
          <ToggleButton
            {...register('cleanupEnabled')}
            title={i18nSettings.t('cleanup_enabled')}
            disabled={!auditSettings?.isJobEnabledGlobally}
            hint={
              auditSettings?.isJobEnabledGlobally
                ? null
                : i18nSettings.t('cleanup_disabled_globally_hint')
            }
          />
        </Field>
        <Field
          title={i18nSettings.t('delete_after_months')}
          className={styles.field}
        >
          <Input
            variant="normal"
            type="number"
            disabled={!auditSettings?.isJobEnabledGlobally || !cleanupEnabled}
            {...register('deleteAfterMonths', {
              ...requiredRule(),
              ...positiveRule(),
              ...minRule(1),
              ...maxRule(1000),
              valueAsNumber: true,
            })}
            errorText={formState.errors.deleteAfterMonths?.message}
          />
        </Field>
      </div>
    </form>
  );
});
